import { type ApolloError, gql, useQuery } from '@apollo/client';
import * as React from 'react';

import { ONBOARDING_FAMILY_FRAGMENT } from './useOnboardingState';

import type { GetDependentsQuery, GetDependentsQueryVariables } from './__generated__/useDependents.gql';
import type { CaregiverRelationshipKind } from '../../__generated__/globalTypes';

const GET_ONBOARDING_DEPENDENTS = gql`
  query GetDependents {
    getCurrentUser {
      id
    }
    getCurrentFamily {
      ...OnboardingFamilyFragment
    }
  }
  ${ONBOARDING_FAMILY_FRAGMENT}
`;

export interface IDependent {
  id: string;
  /** `CaregiverRelationship` row id — required for `updateCaregiverRelationship`. */
  caregiverRelationshipId: string;
  firstName: string;
  lastName: string;
  preferredName: string | null;
  dateOfBirth: string | null;
  email: string | null;
  phoneNumber: string | null;
  /**
   * When the person on this number last proved they hold it, or `null` when
   * nobody has. An account-backed loved one with `null` here is unreachable by
   * text and cannot be given new work, so the card says so.
   */
  phoneNumberVerifiedAt: string | null;
  /**
   * When the outstanding attempt to prove `phoneNumber` started, or `null` when
   * none is outstanding. Proving a loved one's number runs as a group text
   * rather than a code, so it stays outstanding for as long as both sides take
   * to answer — which is why the card reads a start rather than a flag.
   */
  phoneVerificationStartedAt: string | null;
  /**
   * Whether an account answers for this person. Their contact then lives on
   * that account, so the profile cannot write it — `DependentController.update`
   * refuses the patch and `dependants_linked_contact_chk` refuses it again.
   * Both stay editable here, through `updateDependentEmail` and
   * `updateDependentPhoneNumber`, which write the account instead.
   */
  isAccountBacked: boolean;
  relationship: CaregiverRelationshipKind;
  isStep: boolean;
  isInLaw: boolean;
  /** Whether this dependent lives with the acting caregiver. `null` until the living-situation step is answered. */
  livesWithCaregiver: boolean | null;
}

/** The partnership row joining the user's two dependents, when one exists. */
export interface IDependentPartnership {
  id: string;
  /** Whether the two partnered dependents live together. `null` until answered. */
  liveTogether: boolean | null;
}

export interface IDependentsSnapshot {
  dependents: ReadonlyArray<IDependent>;
  /** True when a partnership row already joins the two ids. False if either id is missing or no row matches. */
  arePartnered: (idA: string, idB: string) => boolean;
}

export interface IUseDependentsResult {
  /**
   * This user's dependents (own caregiver-edge rows only), in API order.
   * Stabilized via `useMemo` so downstream `useEffect([dependents])`
   * consumers don't fire on every render.
   */
  dependents: ReadonlyArray<IDependent>;
  /** True when a partnership row already links the user's two dependents. */
  partnershipExists: boolean;
  /** The partnership row joining the user's two dependents, or `null` when there isn't one. Carries the row id + `liveTogether` for the living-situation step to hydrate and update. */
  partnership: IDependentPartnership | null;
  loading: boolean;
  error: ApolloError | undefined;
  /**
   * Refetches the query and resolves with a snapshot of the latest server
   * state — both the dependents list and a partnership lookup. The review
   * step uses this after Phase 0 / Phase 1 mutations to decide whether to
   * link, since the reactive `partnershipExists` value above is captured at
   * `useCallback` definition time and goes stale during a submit.
   */
  refetch: () => Promise<IDependentsSnapshot>;
}

/**
 * Reads the acting user's dependents for every loved-ones onboarding step
 * (count / entry / review + the contact entry/review pair). Filters
 * caregiver relationships to the user (skips rows owned by a co-caregiver)
 * and projects the relationship metadata onto each dependent alongside the
 * identity + contact columns.
 *
 * Pairs with `useCreateDependentAndRelationship` + `useLinkDependents` +
 * `useUpdateDependent` + `useUpdateDependentContact`. The create + link
 * mutations return the parent `Family` via `OnboardingFamilyFragment`,
 * writing the new `caregiverRelationships` / `dependantPartnerships` lists
 * into the normalized cache. The update mutations write back to the
 * existing `Dependant:<id>` record. Either way Apollo re-broadcasts this
 * query's result without a manual refetch.
 */
const projectDependents = (data: GetDependentsQuery | undefined): ReadonlyArray<IDependent> => {
  const user = data?.getCurrentUser ?? null;
  const family = data?.getCurrentFamily ?? null;
  if (!user || !family) {
    return [];
  }
  return family.caregiverRelationships
    .filter((r) => r.caregiver.id === user.id)
    .map<IDependent>((r) => ({
      id: r.dependant.id,
      caregiverRelationshipId: r.id,
      firstName: r.dependant.firstName,
      lastName: r.dependant.lastName,
      preferredName: r.dependant.preferredName ?? null,
      dateOfBirth: r.dependant.dateOfBirth ? String(r.dependant.dateOfBirth) : null,
      email: r.dependant.email ?? null,
      phoneNumber: r.dependant.phoneNumber ?? null,
      phoneNumberVerifiedAt: r.dependant.phoneNumberVerifiedAt ?? null,
      phoneVerificationStartedAt: r.dependant.phoneVerificationStartedAt ?? null,
      isAccountBacked: r.dependant.isAccountBacked,
      relationship: r.relationship,
      isStep: r.isStep,
      isInLaw: r.isInLaw,
      livesWithCaregiver: r.livesWithCaregiver ?? null,
    }));
};

const projectPartnership = (
  data: GetDependentsQuery | undefined,
  dependents: ReadonlyArray<IDependent>
): IDependentPartnership | null => {
  if (dependents.length !== 2) {
    return null;
  }
  const partnerships = data?.getCurrentFamily?.dependantPartnerships ?? [];
  const match = partnerships.find((p) => {
    const ids = new Set(p.dependants.map((d) => d.id));
    return ids.has(dependents[0].id) && ids.has(dependents[1].id);
  });
  return match ? { id: match.id, liveTogether: match.liveTogether ?? null } : null;
};

const projectSnapshot = (data: GetDependentsQuery | undefined): IDependentsSnapshot => {
  const partnerships = data?.getCurrentFamily?.dependantPartnerships ?? [];
  const partneredPairs = partnerships.map((p) => new Set(p.dependants.map((d) => d.id)));
  return {
    dependents: projectDependents(data),
    arePartnered: (idA, idB) => partneredPairs.some((pair) => pair.has(idA) && pair.has(idB)),
  };
};

export const useDependents = (): IUseDependentsResult => {
  const { data, loading, error, refetch } = useQuery<GetDependentsQuery, GetDependentsQueryVariables>(
    GET_ONBOARDING_DEPENDENTS,
    { fetchPolicy: 'cache-and-network' }
  );

  const family = data?.getCurrentFamily ?? null;

  const dependents = React.useMemo<ReadonlyArray<IDependent>>(() => projectDependents(data), [data]);

  const partnershipExists = React.useMemo(() => {
    if (!family || dependents.length !== 2) {
      return false;
    }
    return family.dependantPartnerships.some((p) => {
      const ids = new Set(p.dependants.map((d) => d.id));
      return ids.has(dependents[0].id) && ids.has(dependents[1].id);
    });
  }, [family, dependents]);

  const partnership = React.useMemo(() => projectPartnership(data, dependents), [data, dependents]);

  const refetchAndProject = React.useCallback(async () => {
    const result = await refetch();
    return projectSnapshot(result.data);
  }, [refetch]);

  return { dependents, partnershipExists, partnership, loading, error, refetch: refetchAndProject };
};
